import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useCompleteRegister from "../../../api/auth/register/useCompleteRegister";
import sandClock from "../../../assets/images/icons/sandClock.svg";
import {
  FileUpload,
  GeneralButton,
  GeneralSelect,
  GeneralSelectApi,
  Loader,
  Modal,
} from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { OptionType } from "../../../interface/OptionTypes";
import { setUserDetails } from "../../../redux/reducers/registrationSlice";
import {
  setPassedOptional,
  setRegistrationComplete,
} from "../../../redux/reducers/userSlice";
import { capitalizeEachWord } from "../../../utils/utils";
import AuthLayout from "../../../Layout/AuthLayout";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { FILETYPES } from "../../../Constants/fileConstants";

const UploadDegreeDocument: React.FC = () => {
  const userAuth = useAppSelector((state) => state.user);
  const userDetails = useAppSelector((state) => state.registration.userDetails);
  const [errorMessageKey, setErrorMessageKey] = useState<string | null>(null);

  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [file, setFile] = useState<File | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(
    null,
  );
  const [selectedSubspecialty, setSelectedSubspecialty] = useState<
    string | null
  >(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasSubSpecialties, setHasSubSpecialties] = useState<boolean>(false);

  useEffect(() => {
    if (userAuth.isRegistrationComplete) {
    }
  }, [userAuth.isRegistrationComplete]);

  // Initialize the mutation for completing registration
  const { mutate: completeRegister, isPending: isSubmitting } =
    useCompleteRegister();

  // Handle file change with validation
  const handleFileChange = (uploadedFile: File) => {
    setFile(uploadedFile);
    dispatch(setUserDetails({ specialtyFile: uploadedFile }));
    setUploadSuccess(true);
    setErrorMessageKey(null);
  };

  // Handle Specialty Change
  const handleSpecialtyChange = (selectedOption: any) => {
    if (selectedOption) {
      const specialtyId = selectedOption.value.toString();
      setSelectedSpecialty(specialtyId);
      dispatch(setUserDetails({ SpecialtyId: specialtyId }));

      if (selectedOption.hasChildren) {
        setHasSubSpecialties(true);
        setSelectedSubspecialty(null);
        dispatch(setUserDetails({ SubSpecialtyId: null }));
      } else {
        setHasSubSpecialties(false);
        setSelectedSubspecialty(null);
        dispatch(setUserDetails({ SubSpecialtyId: null }));
      }
    } else {
      setSelectedSpecialty(null);
      setHasSubSpecialties(false);
      setSelectedSubspecialty(null);
      dispatch(setUserDetails({ SpecialtyId: null, SubSpecialtyId: null }));
    }
  };

  // Handle Subspecialty Change
  const handleSubspecialtyChange = (selectedOption: OptionType | null) => {
    if (selectedOption) {
      setSelectedSubspecialty(selectedOption.value.toString());
      dispatch(
        setUserDetails({ SubSpecialtyId: selectedOption.value.toString() }),
      );
    } else {
      setSelectedSubspecialty(null);
      dispatch(setUserDetails({ SubSpecialtyId: null }));
    }
  };

  const handleCancel = () => {
    setFile(null);
    setUploadSuccess(false);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const { AccountTypeId, Id, FullName } = userDetails;

    if (!Id || !FullName || !AccountTypeId || !selectedSpecialty) {
      setErrorMessageKey("missing_required_fields");
      return;
    }

    if (file) {
      const payload: any = {
        AccountTypeId: AccountTypeId,
        SpecialityId: selectedSubspecialty || selectedSpecialty, // Use subspecialty ID if available
        File: file,
      };

      completeRegister(payload, {
        onSuccess: (response) => {
          if (response && response.Status) {
            if (response.Data) {
              setIsModalOpen(true);
              dispatch(
                setUserDetails({
                  FullName: response.Data.FullName,
                  FullNameAr: response.Data.FullNameAr,
                  FullNameEn: response.Data.FullNameEn,
                  Id: response.Data.Id,
                  PhoneNumber: response.Data.PhoneNumber,
                }),
              );

              dispatch(setRegistrationComplete(response.Data.IsCompleted));
            } else {
              setIsModalOpen(true);
            }
          }
        },
      });
    }
  };

  const formMethods = useForm();
  const { AccountTypeId } = userDetails;

  return (
    <AuthLayout
      title={`${t("just_welcome")} ${t("dr")} ${capitalizeEachWord(userDetails.FullName)} 👨🏻‍⚕️🩺`}
      lottieImage={null}
      description={t("upload_degree_msg")}
    >
      <FormProvider {...formMethods}>
        <form
          onSubmit={handleSubmit}
          className="flex w-full flex-col items-center gap-6"
        >
          <GeneralSelectApi
            endpoint={EndPoints.SelectSpecialtyType}
            allParams={{
              lang: i18n.language,
              AccountTypeId: AccountTypeId,
            }}
            name="SpecialtyId"
            className="!w-full"
            onChange={handleSpecialtyChange}
            placeholder={t("select_specialty")}
            errors={formMethods.formState.errors}
          />

          {selectedSpecialty && hasSubSpecialties && (
            <GeneralSelectApi
              endpoint={EndPoints.SelectSpecialtyType}
              className="!w-full"
              allParams={{
                ParentSpecialtyId: selectedSpecialty || null,
                lang: i18n.language,
              }}
              name="SubspecialtyId"
              onChange={handleSubspecialtyChange}
              placeholder={t("select_subspecialty") + " " + t("optional")}
            />
          )}

          <FileUpload
            file={file}
            onFileChange={handleFileChange}
            onCancel={handleCancel}
            uploadSuccess={uploadSuccess}
            errorMessageKey={errorMessageKey || ""}
            label={t("doctor_specialty_degree")}
            accept={FILETYPES}
          />

          <GeneralButton
            type="submit"
            title={isSubmitting ? <Loader /> : t("done")}
            color="blue"
            block
            disabled={!file || !selectedSpecialty || isSubmitting}
          />
        </form>
      </FormProvider>
      <Modal isOpen={isModalOpen}>
        <div className="flex flex-col items-center justify-center">
          <img src={sandClock} className="h-28" alt="" />
          <h3 className="mb-2 mt-4 text-lg font-bold">
            {" "}
            {t("your_files_under_review")}
          </h3>
          <p className="w-[80%] text-center text-sm text-body-text">
            {t("you_will_receive_notification")}
          </p>
          <GeneralButton
            onClick={() => dispatch(setPassedOptional(true))}
            route="/"
            title={t("understand")}
            styleClasses="mb-4 mt-8"
          />
        </div>
      </Modal>
    </AuthLayout>
  );
};

export default UploadDegreeDocument;
